// getPag.js
'use strict';

import { LRUCache } from 'lru-cache';

import { getHeaders } from '../resource/fetchOptionHeader.js';
import responseDecryptionUtil from '../resource/responseDecryptionUtil.js';
import env from '../resource/env.js';
import { animation as animationLog } from '../resource/debug.js';

let CACHED_PAG_MODULE = null;

// No `Aborted` here: `/Aborted/i` also matches a normal AbortError and would
// wrongly wipe the cache via the global unhandledrejection listener. The real
// Emscripten abort ("Aborted(RuntimeError: unreachable)") is caught by `unreachable`.
const FATAL_WASM_ERROR_PATTERN =
  /memory access out of bounds|unreachable|function signature mismatch|table index is out of bounds/i;

/**
 * Discard the libpag module immediately when a fatal WASM error is seen, so the
 * next operation runs on a fresh instance instead of the corrupted one.
 * @param {*} error - the caught error / rejection reason.
 */
function recoverPagModuleIfFatal(error) {
  const isFatal =
    (typeof WebAssembly !== 'undefined' &&
      error instanceof WebAssembly.RuntimeError) ||
    FATAL_WASM_ERROR_PATTERN.test(error?.message || '');
  if (isFatal) {
    animationLog('fatal libpag WASM error → resetting module', {
      message: error?.message,
    });
    resetPagModule();
  }
}

// Catch the async OOB/abort that surfaces inside libpag's own rAF flush loop
// (not awaited by us) so we recover immediately rather than only on next call.
if (typeof window !== 'undefined') {
  window.addEventListener('unhandledrejection', event =>
    recoverPagModuleIfFatal(event?.reason)
  );
}

// libpag is a single shared WASM instance: running init/stop/play/destroy
// concurrently corrupts its heap. Funnel every WASM-touching call through this
// serial chain so only one runs at a time (old view torn down before new init).
let pagExecuteChain = Promise.resolve();

/**
 * Run a libpag call strictly after all previously-enqueued ones (never
 * concurrently). Returns a promise that settles with its result.
 * @param {Function} task - the libpag call to run; may be sync or async.
 * @return {Promise<*>}
 */
function executePagPromise(task) {
  const result = pagExecuteChain.then(() => task());
  pagExecuteChain = result.catch(() => {});
  return result;
}

const cache = new LRUCache({
  max: 20,
  dispose: pagFile => {
    try {
      pagFile?.destroy?.();
    } catch (_) {
      // ignore WASM destroy error
    }
  },
});

// Cache raw PAG binary so that callers with shouldUseCache: false (e.g.,
// layer-replacing PAGCanvas) can still avoid re-downloading the same file.
const binaryCache = new LRUCache({ max: 20 });

/**
 * Discard the libpag module and every PAGFile bound to its (now invalid) heap,
 * so the next getPag() re-initializes a fresh module. Safe to call anytime.
 * @return {void}
 */
export function resetPagModule() {
  CACHED_PAG_MODULE = null;
  pagExecuteChain = Promise.resolve();
  try {
    cache.clear();
  } catch (_) {
    // ignore — disposing files from a dead module may throw
  }
}

/**
 * get Pag binary data
 * @param {string} imageSource - PAG file URL
 */
async function fetchPagBinary(imageSource) {
  const isPublicAsset = imageSource?.includes(env.PUBLIC_URL_PREFIX);
  const fetchOptions = { method: 'GET', headers: getHeaders() };

  if (isPublicAsset && env.FEATURE_RESOURCE_DECRYPTION) {
    const encryptedUrl = imageSource.replace(
      env.PUBLIC_URL_PREFIX,
      env.PUBLIC_ENCRYPTED_URL_PREFIX
    );
    const response = await fetch(encryptedUrl, fetchOptions);
    const decryptedData = await responseDecryptionUtil({ response });
    return decryptedData.buffer;
  }

  const response = await fetch(imageSource, isPublicAsset ? fetchOptions : {});
  const buffer = await response.arrayBuffer();
  return buffer;
}

/**
 * initialize PAG module
 */
async function initializePagModule() {
  if (CACHED_PAG_MODULE) return CACHED_PAG_MODULE;

  const { PAGInit } = await import('libpag');
  // Configure WASM file location for Next.js
  let wasmUrl;
  try {
    const wasmModule = await import('libpag/lib/libpag.wasm');
    wasmUrl = wasmModule?.default || wasmModule;
  } catch (_) {
    // Fallback if direct import fails
  }

  CACHED_PAG_MODULE = await PAGInit({
    locateFile: fileName => {
      if (fileName.endsWith('.wasm') && wasmUrl) {
        return wasmUrl;
      }
      // Try to construct path based on current location
      try {
        if (wasmUrl) {
          const url = new URL(wasmUrl, window.location.origin);
          const directoryPath = url.pathname.replace(/[^/]*$/, '');
          return `${directoryPath}${fileName}`;
        }
      } catch (_) {
        // Fallback to default
      }
      return fileName;
    },
  });
  return CACHED_PAG_MODULE;
}

/**
 * create PAG player
 *
 * The returned function accepts an optional `initOptions` that is forwarded to
 * `PAGView.init` (e.g. `{ useScale: false }` so libpag keeps the caller-set
 * canvas backing instead of re-deriving it from getComputedStyle).
 */
function createPagPlayer(pagFile, pagModule, duration) {
  return async (canvasElement, initOptions) => {
    if (!canvasElement) return null;

    // init (+ scale mode) runs on the serial chain so it never overlaps the
    // previous view's stop/destroy on the shared WASM instance.
    let pagView;
    try {
      pagView = await executePagPromise(async () => {
        const view = await pagModule.PAGView.init(
          pagFile,
          canvasElement,
          initOptions
        );
        try {
          const scaleMode = pagModule.ScaleMode?.Zoom ?? 2;
          view.setScaleMode(scaleMode);
        } catch (_) {
          // ignore API difference
        }
        return view;
      });
    } catch (error) {
      recoverPagModuleIfFatal(error);
      throw error;
    }
    let isDisposed = false;

    const safeCall = (fn, ...args) => {
      if (isDisposed) return;
      try {
        return fn.apply(pagView, args);
      } catch (error) {
        recoverPagModuleIfFatal(error);
      }
    };

    // Serialize play/stop (they trigger WASM flushes). Only `play` bails when
    // disposed — `stop` must still run before the queued destroy, else destroy
    // hits OOB (dispose() flips isDisposed sync, so guarding stop would skip it).
    const executePagView = method => () =>
      executePagPromise(async () => {
        if (method === 'play' && isDisposed) return;
        try {
          await pagView[method]();
        } catch (error) {
          recoverPagModuleIfFatal(error);
        }
      });

    return {
      _view: pagView,
      isDisposed: () => isDisposed,
      play: executePagView('play'),
      stop: executePagView('stop'),
      isPlaying: () => safeCall(pagView.isPlaying) || false,
      setRepeatCount: count => safeCall(pagView.setRepeatCount, count),
      getDuration: () => duration,
      dispose: () => {
        if (isDisposed) return;
        isDisposed = true;

        // Destroy on the chain too — it runs only after this view's own
        // stop (enqueued just before by cleanupPlayer) has finished, so the
        // flush loop is halted and we avoid "memory access out of bounds".
        executePagPromise(() => {
          try {
            pagView.destroy();
          } catch (error) {
            recoverPagModuleIfFatal(error);
          }
        });
      },
    };
  };
}

/**
 * Destroy a mutated (skipCache) PAGFile on the libpag serial chain, after every
 * call already enqueued — e.g. its view's stop()/destroy() — has run.
 * Destroying it synchronously off-chain while the view's flush loop is still
 * live frees the composition mid-render and triggers "memory access out of
 * bounds". binaryCache keeps the next reload cheap, so deferring is free.
 * @param {Object} params
 * @param {Object} [params.pagFile] - the PAGFile to destroy; no-op if falsy.
 * @return {void}
 * @example
 * // after player.stop() + player.dispose() have been enqueued:
 * destroyPagFileOnChain({ pagFile });
 */
export function destroyPagFileOnChain({ pagFile } = {}) {
  if (!pagFile?.destroy) return;
  executePagPromise(() => {
    try {
      pagFile.destroy();
    } catch (error) {
      recoverPagModuleIfFatal(error);
    }
  });
}

/**
 * Check if PAG is in cache (without fetching)
 * @param {string} imageSource - PAG file URL
 * @return {boolean} true if PAG is cached
 */
export function isPagCached(imageSource) {
  if (!imageSource || typeof window === 'undefined') {
    return false;
  }
  return cache.has(imageSource);
}

/**
 * Resolves once every libpag call queued so far has finished (i.e. libpag is
 * idle). Callers await this before creating a new view — then re-check their
 * own mounted state — so they never open a WebGL context that is immediately
 * discarded. See PAGCanvas.
 * @return {Promise}
 */
export function waitForPagExecuteChain() {
  return pagExecuteChain;
}

/**
 * Get PAG animation resource.
 *
 * Cache is split into two layers:
 * - binaryCache: always caches the raw .pag bytes (immutable, safe to share).
 * - cache (PAGFile): caches the parsed PAGFile, only safe for read-only
 *   consumers (e.g. gift animations that everyone sees identically).
 *
 * Set `shouldUseCache=false` when the caller needs to call `replaceText` on the
 * returned PAGFile (e.g. entrance animation injecting the viewer's level and
 * username). Such PAGFiles are mutated in place and must not be shared — they
 * are re-loaded from the cached binary each time.
 *
 * @param {Object} params - parameters object
 * @param {string} [params.imageSource] - PAG file URL
 * @param {boolean} [params.shouldUseCache=true] - set to false when the caller
 *   will mutate the PAGFile via layer replacement
 * @return {Promise<Object>} PAG object
 */
async function getPag({ imageSource, shouldUseCache = true } = {}) {
  if (!imageSource) {
    return { isPAGSupported: false, pag: null };
  }

  try {
    if (typeof window === 'undefined') {
      return { isPAGSupported: false, pag: null };
    }

    const pagModule = await initializePagModule();

    if (shouldUseCache) {
      const cached = cache.get(imageSource);
      if (cached) {
        return {
          isPAGSupported: true,
          pag: {
            file: cached,
            width: cached.width(),
            height: cached.height(),
            duration: cached.duration(),
            getPlayer: createPagPlayer(cached, pagModule, cached.duration()),
          },
        };
      }
    }

    let binaryData = binaryCache.get(imageSource);
    if (!binaryData) {
      binaryData = await fetchPagBinary(imageSource);
      if (binaryData?.byteLength) {
        binaryCache.set(imageSource, binaryData);
      }
    }

    if (!binaryData?.byteLength) {
      return { isPAGSupported: false, pag: null };
    }

    const pagFile = await pagModule.PAGFile.load(binaryData);

    if (shouldUseCache) {
      cache.set(imageSource, pagFile);
    }

    return {
      isPAGSupported: true,
      pag: {
        file: pagFile,
        width: pagFile.width(),
        height: pagFile.height(),
        duration: pagFile.duration(),
        getPlayer: createPagPlayer(pagFile, pagModule, pagFile.duration()),
      },
    };
  } catch (error) {
    recoverPagModuleIfFatal(error);
    return { isPAGSupported: false, pag: null };
  }
}

export default getPag;
